// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE (Supabase migration V2 - PREVIEW ONLY)
// version  : 1.1
// updated  : 2026-06-20
// protected: NO
// changelog: v1.1 (Fix Batch UAT-01 D/E/F/G) - pra-cek QR (tolak MoU sebelum masuk daftar),
//            Total Harga otomatis dari harga jual store + diskon, add/remove berbasis QR unik,
//            kamera HP hanya untuk scan QR (tanpa logic bisnis).
// =============================================================================
// Kasir Penjualan: scan item + diskon per-item + Jual/Compliment -> invoice (Formula F1/F2 backend).
import React, { useEffect, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { ScanLine, ShoppingCart, Loader2, X, ReceiptText, Camera, Clock } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useV2Auth } from "@/v2/V2AuthContext";
import { rupiah } from "@/v2/components/helpers";
import { QrCameraButton } from "@/v2/components/QrCameraButton";
import { solutionRoute } from "@/v2/components/StockAlertAction";
import { useKimi } from "@/v2/components/KimiProvider";

export default function PenjualanPage() {
  const { api, me, storeComplimentEnabled } = useV2Auth();
  const navigate = useNavigate();
  // S-M Batch 2: tautan "Kenapa begini?" pada toast penolakan — HANYA muncul bila KIMI
  // aktif (kimiEnabled) DAN item tidak sudah punya tombol solusi lain (action="fix-merch"/
  // "store-task" tetap diprioritaskan, satu toast Sonner cuma punya satu slot aksi).
  // `openPanel` hanya dipanggil dari onClick tombol aksi toast ini sendiri (klik pengguna).
  const { kimiEnabled, openPanel } = useKimi();
  // S-B (B-4): tombol solusi pada toast penolakan bila solusinya jelas & tunggal.
  // Tombol hanya MEMBUKA layar (pakai solutionRoute dari StockAlertAction — satu sumber).
  // S-M (Batch 1, bagian C): `description` opsional -> dipakai untuk menaruh daftar per-item
  // DI BAWAH kalimat ringkasan (title), memakai layout dua-tingkat bawaan Sonner. Tidak ada
  // penggabungan string manual -> tidak ada risiko tampil dobel.
  const toastReject = (msg, action, productId, description, helpUrl) => {
    const route = solutionRoute(action, productId);
    const opts = {};
    if (route) {
      opts.action = { label: action === "store-task" ? "Buka Tugas Store" : "Perbaiki di Merchandize",
                       onClick: () => navigate(route) };
    } else if (helpUrl === "kimi" && kimiEnabled) {
      // S-M Batch 2 (9e): tidak ada solusi tunggal yang bisa dinavigasi -> tawarkan
      // penjelasan KIMI memakai kalimat penolakan ini sebagai konteks.
      opts.action = { label: "Kenapa begini?", onClick: () => openPanel({ reason: msg, action }) };
    }
    if (description) opts.description = description;
    toast.error(msg, Object.keys(opts).length ? opts : undefined);
  };
  // UAT-FIX-4 (2): Compliment via kasir dinonaktifkan SEMENTARA HANYA untuk Staff Store
  // (via STORE_COMPLIMENT_ENABLED). Kepala Cabang tidak terpengaruh.
  const isStaffStore = ["STAFF_STORE_AREMA", "STAFF_STORE_BORNEO"].includes(me?.role);
  const complimentBlocked = isStaffStore && !storeComplimentEnabled;
  const [tiers, setTiers] = useState([]);
  const [methods, setMethods] = useState([]);
  const [marketplaceMethods, setMarketplaceMethods] = useState([]);
  const [rows, setRows] = useState([]); // {qr, tier, name, size, price}
  const [scan, setScan] = useState("");
  const [txnType, setTxnType] = useState("Jual");
  const [method, setMethod] = useState("Cash");
  const [note, setNote] = useState("");
  const [orderId, setOrderId] = useState("");
  const [txnNo, setTxnNo] = useState("");
  const [payNote, setPayNote] = useState("");
  const [email, setEmail] = useState("");      // UAT-03 C: email customer (opsional)
  const [promo, setPromo] = useState(false);   // consent promo (default off)
  // Bayar Nanti (Piutang): hanya Staff Store + transaksi Jual. credit_for WAJIB.
  const [creditSale, setCreditSale] = useState(false);
  const [creditFor, setCreditFor] = useState("");
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [invoice, setInvoice] = useState(null);
  // SLICE 3: pengaturan PPN store (default nonaktif). Backend adalah sumber kebenaran; ini utk estimasi kasir.
  const [tax, setTax] = useState({ enabled: false, percent: 0 });

  useEffect(() => {
    api("/api/v2/sales/discount-tiers").then(async (r) => {
      if (r.ok) {
        const d = await r.json();
        setTiers(d.tiers); setMethods(d.payment_methods);
        setMarketplaceMethods(d.marketplace_methods || []);
      }
    });
    // best-effort: role non-store bisa 400 -> anggap tanpa pajak
    api("/api/v2/store-tax-settings").then(async (r) => {
      if (r.ok) { const d = await r.json(); setTax({ enabled: !!d.enabled, percent: Number(d.percent) || 0 }); }
    }).catch(() => {});
  }, [api]);

  const isMarketplace = marketplaceMethods.includes(method);
  const tierPct = useCallback((id) => (tiers.find((t) => t.id === id)?.percent || 0), [tiers]);

  // Subtotal per item (mengikuti formula backend: diskon dibulatkan dari harga jual store).
  const lineTotal = useCallback((r) => {
    const pct = txnType === "Compliment" ? 0 : tierPct(r.tier);
    const price = r.price || 0;
    return price - Math.round((price * pct) / 100);
  }, [txnType, tierPct]);

  // Total Harga otomatis (read-only). Compliment = 0 (tidak masuk omzet).
  const totalHarga = txnType === "Compliment" ? 0 : rows.reduce((s, r) => s + lineTotal(r), 0);
  // SLICE 3: estimasi PPN (SESUDAH diskon), hanya utk Jual & bila pajak store aktif. Nilai final tetap backend.
  // Bayar Nanti tetap Jual -> PPN masuk ke total piutang.
  const taxActive = txnType === "Jual" && tax.enabled && tax.percent > 0;
  const ppnEstimate = taxActive ? Math.round((totalHarga * tax.percent) / 100) : 0;
  const grandTotal = totalHarga + ppnEstimate;

  // Tambah QR ke daftar kasir (input manual / scan-gun / kamera HP — semua lewat sini).
  // E: pra-cek ke backend; MoU / item invalid DITOLAK & tidak masuk daftar.
  // F: simpan harga jual store untuk Total Harga otomatis. Dedup QR unik (G).
  const addQrCode = useCallback(async (raw) => {
    const q = (raw || "").trim();
    if (!q) return;
    let dup = false;
    setRows((prev) => { dup = !!prev.find((x) => x.qr === q); return prev; });
    if (dup) { toast.info(`QR sudah ada di daftar: ${q}`); return; }
    setChecking(true);
    try {
      const r = await api(`/api/v2/sales/lookup?qr=${encodeURIComponent(q)}`);
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.valid) {
        toastReject(d?.reason || "QR tidak valid / tidak bisa dijual.", d?.action, d?.product_id, undefined, d?.help_url);
        return; // tidak menambahkan ke daftar
      }
      setRows((prev) => prev.find((x) => x.qr === q)
        ? prev
        : [...prev, { qr: q, tier: "none", name: d.product_name, size: d.size, price: d.unit_price,
                      base_price: d.base_price ?? d.unit_price, size_surcharge: d.size_surcharge ?? 0 }]);
      toast.success(`Ditambahkan: ${d.product_name || q}`);
    } catch {
      toast.error("Gagal memeriksa QR. Coba lagi.");
    } finally {
      setChecking(false);
    }
  }, [api]);

  const addQr = async () => { const v = scan; setScan(""); await addQrCode(v); };
  const setTier = (qr, tier) => setRows((rs) => rs.map((r) => r.qr === qr ? { ...r, tier } : r));
  const remove = (qr) => setRows((rs) => rs.filter((r) => r.qr !== qr));

  const submit = async () => {
    if (rows.length === 0) { toast.error("Scan minimal 1 item"); return; }
    if (txnType === "Compliment" && complimentBlocked) { toast.error("Fitur Compliment sedang dinonaktifkan sementara"); return; }
    if (txnType === "Compliment" && !note.trim()) { toast.error("Isi keterangan compliment"); return; }
    if (creditSale && !creditFor.trim()) { toast.error("Isi 'Untuk Siapa' untuk transaksi Bayar Nanti"); return; }
    setBusy(true);
    try {
      const body = {
        transaction_type: txnType, payment_method: method, compliment_note: note,
        amount_paid: creditSale ? 0 : grandTotal, // Bayar Nanti: belum ada uang masuk; termasuk PPN bila ada
        credit_sale: creditSale, credit_for: creditSale ? creditFor.trim() : "",
        buyer_email: email, promo_consent: promo,
        payment_order_id: isMarketplace ? orderId : "",
        payment_txn_no: isMarketplace ? txnNo : "",
        payment_note: payNote,
        items: rows.map((r) => ({ qr_id: r.qr, discount_tier: r.tier })),
      };
      const r = await api("/api/v2/sales", { method: "POST", body: JSON.stringify(body) });
      const data = await r.json();
      if (!r.ok) {
        const dd = data.detail;
        if (typeof dd === "string") {
          toastReject(dd);
        } else {
          // S-M (Batch 1, bagian C): kalimat ringkasan (dd.message) — bila ada — jadi KEPALA
          // pesan (title toast); daftar per item jadi description DI BAWAHNYA (Sonner 2-tingkat
          // bawaan). Bila tidak ada message, perilaku persis seperti sebelumnya (daftar saja).
          const list = dd?.errors?.map((e) => `${e.qr_id || e.product_name}: ${e.reason}`).join("; ");
          const pid = dd?.product_id || dd?.errors?.find((e) => e.product_id)?.product_id;
          const action = dd?.action || dd?.errors?.find((e) => e.action)?.action;
          if (dd?.message) {
            toastReject(dd.message, action, pid, list || undefined);
          } else {
            toastReject(list || "Gagal", action, pid);
          }
        }
        return;
      }
      setInvoice(data); setRows([]); setNote("");
      setOrderId(""); setTxnNo(""); setPayNote(""); setEmail(""); setPromo(false);
      setCreditSale(false); setCreditFor("");
      const emailMsg = data.email_status === "pending"
        ? " · Email customer tersimpan (akan dikirim saat layanan email aktif)"
        : "";
      if (data.credit_sale) {
        toast.success(`Bayar Nanti tercatat · Invoice ${data.invoice_no} · ${rupiah(data.total)} (belum lunas)`);
      } else {
        toast.success(`Invoice ${data.invoice_no} · ${rupiah(data.total)}${emailMsg}`);
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="v2-penjualan-page">
      <h2 className="text-2xl font-semibold">Kasir Penjualan</h2>
      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="p-5 rounded-2xl lg:col-span-2">
          <Tabs value={txnType} onValueChange={(v) => { setTxnType(v); if (v === "Compliment") { setCreditSale(false); setCreditFor(""); } }} className="mb-4">
            <TabsList>
              <TabsTrigger value="Jual" data-testid="v2-sale-jual">Jual</TabsTrigger>
              <TabsTrigger value="Compliment" data-testid="v2-sale-compliment" disabled={complimentBlocked}>Compliment</TabsTrigger>
            </TabsList>
          </Tabs>
          {complimentBlocked && (
            <p className="-mt-2 mb-3 text-xs text-[hsl(var(--badge-warning))]" data-testid="v2-sale-compliment-disabled-note">
              Compliment sementara dinonaktifkan.
            </p>
          )}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <ScanLine className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={scan} placeholder="Scan / ketik QR item lalu Enter" className="pl-9" data-testid="v2-sale-scan"
                onChange={(e) => setScan(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addQr(); } }} />
            </div>
            <Button variant="outline" onClick={addQr} disabled={checking} data-testid="v2-sale-add">
              {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : "Tambah"}
            </Button>
            <QrCameraButton onScan={addQrCode} testid="v2-sale-cam" />
          </div>
          <p className="text-[11px] text-muted-foreground mt-2 flex items-center gap-1">
            <Camera className="w-3 h-3" /> Kamera HP hanya untuk membaca QR item. Diskon, metode &amp; pembayaran tetap diatur di halaman ini.
          </p>
          {/* Desktop: tabel */}
          <div className="hidden md:block">
          <Table className="mt-4">
            <TableHeader><TableRow>
              <TableHead>Item</TableHead><TableHead>Harga</TableHead><TableHead>Diskon</TableHead>
              <TableHead className="text-right">Subtotal</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-4 text-sm">Belum ada item. Scan QR item JUAL untuk menambahkan.</TableCell></TableRow>}
              {rows.map((r) => (
                <TableRow key={r.qr} data-testid="v2-sale-row">
                  <TableCell>
                    <div className="font-medium text-sm">{r.name || "-"}{r.size ? ` · ${r.size}` : ""}</div>
                    <div className="font-mono text-[10px] text-muted-foreground">{r.qr}</div>
                  </TableCell>
                  <TableCell className="text-sm" data-testid="v2-sale-price">
                    {rupiah(r.price)}
                    {r.size_surcharge > 0 && (
                      <div className="text-[11px] text-muted-foreground" data-testid="v2-sale-surcharge">
                        normal {rupiah(r.base_price)} + size {r.size} {rupiah(r.size_surcharge)}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Select value={r.tier} onValueChange={(v) => setTier(r.qr, v)} disabled={txnType === "Compliment"}>
                      <SelectTrigger className="h-8 w-44" data-testid="v2-sale-tier"><SelectValue /></SelectTrigger>
                      <SelectContent>{tiers.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right text-sm font-medium" data-testid="v2-sale-subtotal">{rupiah(txnType === "Compliment" ? 0 : lineTotal(r))}</TableCell>
                  <TableCell><Button size="icon" variant="ghost" onClick={() => remove(r.qr)} data-testid="v2-sale-remove" title="Hapus item"><X className="w-4 h-4" /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>

          {/* Mobile: card list (semua data tetap tampil) */}
          <div className="md:hidden mt-4 space-y-3" data-testid="v2-sale-cards">
            {rows.length === 0 && (
              <div className="text-center text-muted-foreground py-6 text-sm rounded-xl border border-dashed">
                Belum ada item. Scan QR item JUAL untuk menambahkan.
              </div>
            )}
            {rows.map((r) => (
              <div key={r.qr} className="rounded-xl border p-3 space-y-2" data-testid="v2-sale-row-m">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{r.name || "-"}{r.size ? ` · ${r.size}` : ""}</div>
                    <div className="font-mono text-[10px] text-muted-foreground break-all">{r.qr}</div>
                  </div>
                  <Button size="icon" variant="ghost" className="shrink-0 h-9 w-9" onClick={() => remove(r.qr)} data-testid="v2-sale-remove-m" title="Hapus item"><X className="w-4 h-4" /></Button>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Harga</span>
                  <span className="text-right" data-testid="v2-sale-price-m">{rupiah(r.price)}</span>
                </div>
                {r.size_surcharge > 0 && (
                  <div className="text-[11px] text-muted-foreground text-right" data-testid="v2-sale-surcharge-m">
                    normal {rupiah(r.base_price)} + size {r.size} {rupiah(r.size_surcharge)}
                  </div>
                )}
                <div>
                  <Label className="text-[11px] text-muted-foreground">Diskon</Label>
                  <Select value={r.tier} onValueChange={(v) => setTier(r.qr, v)} disabled={txnType === "Compliment"}>
                    <SelectTrigger className="h-10 w-full mt-1" data-testid="v2-sale-tier-m"><SelectValue /></SelectTrigger>
                    <SelectContent>{tiers.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between border-t pt-2">
                  <span className="text-sm text-muted-foreground">Subtotal</span>
                  <span className="text-sm font-semibold" data-testid="v2-sale-subtotal-m">{rupiah(txnType === "Compliment" ? 0 : lineTotal(r))}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5 rounded-2xl space-y-3">
          <h3 className="font-semibold">Pembayaran</h3>
          {/* F: Total Harga otomatis (read-only) menggantikan field "Dibayar" */}
          {taxActive && rows.length > 0 && (
            <div className="rounded-xl border p-3 text-sm space-y-1" data-testid="v2-sale-tax-box">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{creditSale ? "Subtotal Piutang" : "Subtotal (setelah diskon)"}</span>
                <span className="tabular-nums" data-testid="v2-sale-dpp">{rupiah(totalHarga)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">PPN {tax.percent}%</span>
                <span className="tabular-nums" data-testid="v2-sale-ppn">{rupiah(ppnEstimate)}</span>
              </div>
            </div>
          )}
          <div className="rounded-xl bg-muted/50 p-3 flex items-center justify-between" data-testid="v2-sale-total-card">
            <span className="text-sm text-muted-foreground">{creditSale ? "Total Piutang" : "Total Harga"}{taxActive ? " (termasuk PPN)" : ""}</span>
            <span className="text-xl font-bold" data-testid="v2-sale-total">{rupiah(grandTotal)}</span>
          </div>
          {txnType === "Jual" ? (
            <>
              {/* Bayar Nanti (Piutang) — hanya Staff Store */}
              {isStaffStore && (
                <div className="rounded-xl border p-3" data-testid="v2-sale-bayar-nanti-box">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-[hsl(var(--badge-warning))]" />
                      <Label className="text-sm">Bayar Nanti (Piutang)</Label>
                    </div>
                    <Switch checked={creditSale} onCheckedChange={setCreditSale} data-testid="v2-sale-bayar-nanti" />
                  </div>
                  {creditSale && (
                    <div className="mt-3 space-y-2 rounded-lg border border-[hsl(var(--badge-warning))]/50 bg-[hsl(var(--badge-warning))]/10 p-3" data-testid="v2-sale-credit-fields">
                      <Label className="text-xs">Untuk Siapa <span className="text-destructive">*</span></Label>
                      <Input value={creditFor} onChange={(e) => setCreditFor(e.target.value)}
                        placeholder="Nama penerima / klub" data-testid="v2-sale-credit-for" aria-invalid={!creditFor.trim()} />
                      <p className="text-[11px] text-[hsl(var(--badge-warning))]">
                        Barang keluar sekarang, tetapi transaksi ini <strong>belum masuk penagihan</strong> sampai
                        pembayaran diterima Staff Store.
                      </p>
                    </div>
                  )}
                </div>
              )}
              {!creditSale && (<>
              <div><Label className="text-xs">Metode</Label>
                <Select value={method} onValueChange={setMethod}>
                  <SelectTrigger data-testid="v2-sale-method"><SelectValue /></SelectTrigger>
                  <SelectContent>{methods.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {isMarketplace && (
                <div className="space-y-2 rounded-xl border border-dashed p-3" data-testid="v2-sale-marketplace-fields">
                  <Label className="text-xs text-muted-foreground">Detail Marketplace/Website (opsional)</Label>
                  <Input placeholder="Order ID" value={orderId} onChange={(e) => setOrderId(e.target.value)} data-testid="v2-sale-order-id" />
                  <Input placeholder="No. Transaksi" value={txnNo} onChange={(e) => setTxnNo(e.target.value)} data-testid="v2-sale-txn-no" />
                </div>
              )}
              <div><Label className="text-xs">Catatan Pembayaran (opsional)</Label>
                <Input value={payNote} onChange={(e) => setPayNote(e.target.value)} data-testid="v2-sale-pay-note" /></div>
              </>)}
            </>
          ) : (
            <div><Label className="text-xs">Keterangan Compliment (wajib)</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="v2-sale-note" /></div>
          )}
          {/* UAT-03 C: Email customer (opsional) + consent promo */}
          <div className="space-y-2 rounded-xl border p-3" data-testid="v2-sale-customer-box">
            <Label className="text-xs">Alamat Email Customer (opsional)</Label>
            <Input type="email" placeholder="customer@email.com" value={email}
              onChange={(e) => setEmail(e.target.value)} data-testid="v2-sale-email" />
            <label className="flex items-start gap-2 cursor-pointer select-none">
              <input type="checkbox" className="mt-0.5 h-4 w-4 rounded border-input accent-primary"
                checked={promo} onChange={(e) => setPromo(e.target.checked)} data-testid="v2-sale-consent" />
              <span className="text-xs text-muted-foreground">Customer bersedia menerima promo dan informasi dari ETAMS.</span>
            </label>
            <p className="text-[10px] text-muted-foreground">Invoice/struk akan dikirim ke email setelah layanan email aktif. Transaksi tetap diproses meski email kosong.</p>
          </div>
          <Button className="w-full" onClick={submit} disabled={busy || (creditSale && !creditFor.trim())} data-testid="v2-sale-submit">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ShoppingCart className="w-4 h-4 mr-1" />}{creditSale ? "Simpan Bayar Nanti" : "Proses"}
          </Button>
          {invoice && (
            <div className="border-t pt-3 text-sm space-y-1" data-testid="v2-sale-invoice">
              <div className="flex items-center gap-2 font-medium"><ReceiptText className="w-4 h-4" />{invoice.invoice_no}</div>
              <div className="flex justify-between"><span>Subtotal</span><span>{rupiah(invoice.subtotal)}</span></div>
              <div className="flex justify-between"><span>Diskon</span><span>-{rupiah(invoice.discount_total)}</span></div>
              {invoice.tax_amount > 0 && (<>
                <div className="flex justify-between text-muted-foreground"><span>DPP (setelah diskon)</span><span data-testid="v2-sale-inv-dpp">{rupiah(invoice.dpp)}</span></div>
                <div className="flex justify-between text-muted-foreground"><span>PPN {invoice.tax_percent}%</span><span data-testid="v2-sale-inv-ppn">{rupiah(invoice.tax_amount)}</span></div>
              </>)}
              <div className="flex justify-between font-semibold"><span>Total</span><span>{rupiah(invoice.total)}</span></div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
